import React, { useEffect, useRef, useState } from "react";
import Select from "react-select";
import { useNavigate, useOutletContext } from "react-router";
import {
  ITeacherPassage,
  ITeacherPassageSearchRequest,
  getTeacherPassages,
  deleteTeacherPassage,
} from "../../../../api/crmContent.service";
import { getAllgrade } from "../../../../api/grade.service";
import {
  ApproveIcon,
  DeleteIcon,
  Disapprove,
  EditIcon,
  FilterIcon,
  ResetIcon,
} from "../../../../assets/icons/index";
import { ISelectElement } from "../../../../model/interface/keyValuePair";
import Loader from "../../../shared/loader/loader";
import Pagination from "../../../shared/common/pagination";
import { EmptyResults } from "../../../shared/common/emptyResults";
import { InitialsBadge } from "../../../shared/common/initialsBadge";
import SelectPageSize from "../../../shared/common/pageSizeDropdown";
import { SortIcon } from "../../../shared/common/sortIcon";
import { IUserContentOutletContext } from "./UserContentLayout";
import { NewPassageModal } from "./NewPassageModal";
import DeleteModal from "../../../shared/common/deleteModal";

const defaultGradeOption: ISelectElement = { label: "Select", value: "0" };
const defaultPageSize = 20;
const defaultPageNumber = 1;
const defaultOrderByColumn = "Title";

const approvedNextState = (
  current: boolean | undefined
): boolean | undefined => {
  if (current === undefined) return true;
  if (current === true) return false;
  return undefined;
};

const approvedLabel = (current: boolean | undefined): string => {
  if (current === undefined) return "Any";
  return current ? "Yes" : "No";
};

export const Passages: React.FC = () => {
  const { userId, userInfo } = useOutletContext<IUserContentOutletContext>();
  const districtId = userInfo?.districtId;
  const navigate = useNavigate();

  const [loading, setLoading] = useState<boolean>(false);
  const [teacherPassages, setTeacherPassages] = useState<
    Array<ITeacherPassage>
  >([]);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);

  const [filterTeacherPassageId, setFilterTeacherPassageId] =
    useState<string>("");
  const [filterTitle, setFilterTitle] = useState<string>("");
  const [grades, setGrades] = useState<Array<ISelectElement>>([]);
  const [selectedGrade, setSelectedGrade] =
    useState<ISelectElement>(defaultGradeOption);
  const [isApproved, setIsApproved] = useState<boolean | undefined>(undefined);

  const [pageSize, setPageSize] = useState<number>(defaultPageSize);
  const [pageNumber, setPageNumber] = useState<number>(defaultPageNumber);
  const [orderByColumn, setOrderByColumn] =
    useState<string>(defaultOrderByColumn);
  const [sortByDesc, setSortByDesc] = useState<boolean>(false);
  const [resetDropdownFlag, setResetDropdownFlag] = useState<boolean>(false);
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [passageIdToDelete, setPassageIdToDelete] = useState<number | null>(
    null
  );

  const paginationRef = useRef<{ onReset: () => void }>(null);
  const pageSizeRef = useRef<{ onReset: () => void }>(null);
  const isInitialMount = useRef<boolean>(true);

  const buildRequest = (
    overrides?: Partial<ITeacherPassageSearchRequest>
  ): ITeacherPassageSearchRequest => {
    const parsedId = filterTeacherPassageId
      ? Number(filterTeacherPassageId)
      : undefined;
    const gradeIdNum =
      selectedGrade && selectedGrade.value !== "0"
        ? Number(selectedGrade.value)
        : undefined;
    return {
      teacherPassageId: parsedId,
      title: filterTitle || undefined,
      gradeId: gradeIdNum,
      isActive: true,
      isApproved,
      pageSize,
      pageNumber,
      orderByColumn,
      sortByDesc,
      districtId,
      ...overrides,
    };
  };

  const search = (request: ITeacherPassageSearchRequest) => {
    setLoading(true);
    getTeacherPassages(request)
      .then((response) => {
        setTeacherPassages(response.data.data);
        setTotalRecords(response.data.totalRecords);
        setTotalPages(Math.ceil(response.data.totalRecords / request.pageSize));
      })
      .finally(() => setLoading(false));
  };

  const bindGrades = () => {
    getAllgrade().then((response) => {
      const options: Array<ISelectElement> = (response.data?.data ?? []).map(
        (grade: { gradeId: number; name: string }) => ({
          value: String(grade.gradeId ?? 0),
          label: grade.name,
        })
      );
      setGrades([defaultGradeOption, ...options]);
    });
  };

  useEffect(() => {
    bindGrades();
  }, []);

  useEffect(() => {
    if (districtId === undefined) return;
    search({
      pageSize: defaultPageSize,
      pageNumber: defaultPageNumber,
      orderByColumn: defaultOrderByColumn,
      sortByDesc: false,
      districtId,
      isActive: true,
    });
  }, [districtId]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    search(buildRequest({ pageNumber }));
  }, [pageNumber]);

  useEffect(() => {
    if (isInitialMount.current) return;
    paginationRef.current?.onReset();
    search(buildRequest({ pageNumber: defaultPageNumber, pageSize }));
  }, [pageSize]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    paginationRef.current?.onReset();
    pageSizeRef.current?.onReset();
    search(
      buildRequest({
        pageNumber: defaultPageNumber,
        pageSize: defaultPageSize,
      })
    );
  };

  const handleDeleteClick = (passageId: number) => {
    setPassageIdToDelete(passageId);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = () => {
    if (passageIdToDelete == null) return;
    deleteTeacherPassage(passageIdToDelete)
      .then(() => {
        setShowDeleteModal(false);
        setPassageIdToDelete(null);
        search(buildRequest());
      })
      .catch(() => setShowDeleteModal(false));
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setPassageIdToDelete(null);
  };

  const handleReset = () => {
    setFilterTeacherPassageId("");
    setFilterTitle("");
    setSelectedGrade(defaultGradeOption);
    setIsApproved(undefined);
    setResetDropdownFlag((prev) => !prev);
    paginationRef.current?.onReset();
    pageSizeRef.current?.onReset();
    setOrderByColumn(defaultOrderByColumn);
    setSortByDesc(false);
    search({
      pageSize: defaultPageSize,
      pageNumber: defaultPageNumber,
      orderByColumn: defaultOrderByColumn,
      sortByDesc: false,
      districtId,
      isActive: true,
    });
  };

  const handleSortBy = (column: string) => {
    const newSortByDesc = orderByColumn === column ? !sortByDesc : false;
    setOrderByColumn(column);
    setSortByDesc(newSortByDesc);
    search(
      buildRequest({
        orderByColumn: column,
        sortByDesc: newSortByDesc,
      })
    );
  };

  const handleTeacherPassageIdChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value.replace(/[^0-9]/g, "");
    setFilterTeacherPassageId(value);
  };

  const renderSortableHeader = (
    label: string,
    column: string,
    className?: string
  ) => (
    <th
      scope="col"
      className={`px-3 py-2 font-light text-sm text-left text-white dark:text-white tracking-wider whitespace-nowrap${
        className ? ` ${className}` : ""
      }`}
    >
      <span className="flex items-center">
        {label}
        <span
          className="cursor-pointer ml-1 inline-flex"
          onClick={() => handleSortBy(column)}
        >
          <SortIcon
            direction={
              orderByColumn === column ? (sortByDesc ? "desc" : "asc") : "none"
            }
            className="w-2.5 h-4"
          />
        </span>
      </span>
    </th>
  );

  return (
    <>
      {showCreateModal && (
        <NewPassageModal
          userId={userId}
          onClose={() => setShowCreateModal(false)}
        />
      )}
      {showDeleteModal && (
        <DeleteModal
          closeDeleteModal={handleDeleteCancel}
          deleteRecord={handleDeleteConfirm}
          recordName="Teacher Passage"
        />
      )}
      {loading && <Loader />}
      <div className="flex-1 overflow-auto">
        <div className="h-auto min-h-half w-full px-8 py-6">
          <div className="relative py-1">
            <form onSubmit={handleSearch}>
              <div className="pt-6 border-b pb-6">
                <div className="grid grid-cols-12 gap-6">
                  <div className="col-span-3 sm:col-span-3">
                    <label className="text-sm text-gray-700">Passage Id</label>
                    <input
                      id="teacherPassageId"
                      name="teacherPassageId"
                      type="text"
                      inputMode="numeric"
                      className="block mt-1 w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-color focus:border-primary-color dark:focus:border-gray-500 dark:focus:ring-gray-500 dark:bg-custom-black dark:text-white sm:text-sm"
                      value={filterTeacherPassageId}
                      onChange={handleTeacherPassageIdChange}
                    />
                  </div>

                  <div className="col-span-3 sm:col-span-3">
                    <label className="text-sm text-gray-700">Title</label>
                    <input
                      id="title"
                      name="title"
                      type="text"
                      className="block mt-1 w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-color focus:border-primary-color dark:focus:border-gray-500 dark:focus:ring-gray-500 dark:bg-custom-black dark:text-white sm:text-sm"
                      value={filterTitle}
                      onChange={(e) => setFilterTitle(e.target.value)}
                    />
                  </div>

                  <div className="col-span-3 sm:col-span-3">
                    <label className="text-sm text-gray-700">Grade</label>
                    <Select
                      className="w-full mt-1 text-sm"
                      options={grades}
                      onChange={(option) =>
                        setSelectedGrade(option ?? defaultGradeOption)
                      }
                      value={selectedGrade}
                      isSearchable={true}
                      theme={(theme) => ({
                        ...theme,
                        colors: {
                          ...theme.colors,
                          primary25: "#B3B3B3",
                          primary: "#041E42",
                        },
                      })}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-12 gap-6 mt-5">
                  <div className="col-span-3 sm:col-span-3 flex items-center">
                    <input
                      id="isApproved"
                      name="isApproved"
                      type="checkbox"
                      ref={(el) => {
                        if (el) el.indeterminate = isApproved === undefined;
                      }}
                      checked={isApproved === true}
                      onChange={() =>
                        setIsApproved((prev) => approvedNextState(prev))
                      }
                      className="focus:ring-primary-color h-4 w-4 text-primary-color border-gray-300 rounded"
                    />
                    <label className="ml-1.5 text-sm text-gray-700">
                      Approved ({approvedLabel(isApproved)})
                    </label>
                  </div>
                </div>

                <div className="flex justify-between items-center">
                  <div className="flex">
                    <button
                      type="submit"
                      className="mt-4 inline-flex items-center text-sm text-white dark:text-white gap-2 bg-primary-color hover:bg-secondary-color py-1.5 px-4 rounded-lg"
                    >
                      <FilterIcon className="w-3 h-3" />
                      <span>Search</span>
                    </button>
                    <button
                      type="button"
                      onClick={handleReset}
                      className="mt-4 py-1.5 px-4 inline-flex ml-3 items-center text-sm rounded-lg text-gray-700 text-primary-color border border-gray-300 gap-2 bg-white hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-color"
                    >
                      <ResetIcon className="w-3 h-3" />
                      <span>Reset</span>
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(true)}
                    className="mt-4 inline-flex items-center text-sm text-white dark:text-white gap-2 bg-primary-color hover:bg-secondary-color py-1.5 px-4 rounded-lg"
                  >
                    <span>Create Passage</span>
                  </button>
                </div>
              </div>
            </form>

            <div id="dataContainer" className="mt-4">
              {teacherPassages.length === 0 ? (
                <EmptyResults
                  title="No matching passages"
                  subtitle="Adjust your filters and try again."
                />
              ) : (
                <div className="flex flex-col mt-1">
                  <div className="-my-2 relative sm:-mx-6 lg:-mx-8">
                    <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                      <div className="shadow border-b border-gray-200 sm:rounded-lg">
                        <table className="min-w-full table-fixed divide-y divide-gray-200">
                          <thead className="bg-primary-color">
                            <tr>
                              <th
                                scope="col"
                                className="px-3 py-2 w-20 font-light text-sm text-left text-white dark:text-white tracking-wider"
                              >
                                <span className="flex items-center">
                                  Id
                                  <span
                                    className="cursor-pointer ml-1 inline-flex"
                                    onClick={() => handleSortBy("PassageId")}
                                  >
                                    <SortIcon
                                      direction={
                                        orderByColumn === "PassageId"
                                          ? sortByDesc
                                            ? "desc"
                                            : "asc"
                                          : "none"
                                      }
                                      className="w-2.5 h-4"
                                    />
                                  </span>
                                </span>
                              </th>
                              {renderSortableHeader(
                                "Created Date",
                                "CreatedDate",
                                "w-32"
                              )}
                              {renderSortableHeader("Title", "Title")}
                              <th
                                scope="col"
                                className="px-3 py-2 w-16 font-light text-sm text-left text-white dark:text-white tracking-wider"
                              >
                                <span className="flex items-center">
                                  Grade
                                  <span
                                    className="cursor-pointer ml-1 inline-flex"
                                    onClick={() => handleSortBy("Grade")}
                                  >
                                    <SortIcon
                                      direction={
                                        orderByColumn === "Grade"
                                          ? sortByDesc
                                            ? "desc"
                                            : "asc"
                                          : "none"
                                      }
                                      className="w-2.5 h-4"
                                    />
                                  </span>
                                </span>
                              </th>
                              <th
                                scope="col"
                                className="px-3 py-2 w-24 font-light text-sm text-left text-white dark:text-white tracking-wider whitespace-nowrap"
                              >
                                Word Count
                              </th>
                              <th
                                scope="col"
                                className="px-3 py-2 w-16 font-light text-sm text-center text-white dark:text-white tracking-wider"
                              >
                                Active
                              </th>
                              <th
                                scope="col"
                                className="px-3 py-2 w-16 font-light text-sm text-center text-white dark:text-white tracking-wider"
                              >
                                Approved
                              </th>
                              <th
                                scope="col"
                                className="px-3 py-2 w-24 font-light text-sm text-center text-white dark:text-white tracking-wider"
                              >
                                <span className="flex items-center justify-center">
                                  Owner
                                  <span
                                    className="cursor-pointer ml-1 inline-flex"
                                    onClick={() => handleSortBy("Owner")}
                                  >
                                    <SortIcon
                                      direction={
                                        orderByColumn === "Owner"
                                          ? sortByDesc
                                            ? "desc"
                                            : "asc"
                                          : "none"
                                      }
                                      className="w-2.5 h-4"
                                    />
                                  </span>
                                </span>
                              </th>
                              <th
                                scope="col"
                                className="px-3 py-2 w-24 font-light text-sm text-center text-white dark:text-white tracking-wider"
                              >
                                <span className="flex items-center justify-center">
                                  Author
                                  <span
                                    className="cursor-pointer ml-1 inline-flex"
                                    onClick={() => handleSortBy("Author")}
                                  >
                                    <SortIcon
                                      direction={
                                        orderByColumn === "Author"
                                          ? sortByDesc
                                            ? "desc"
                                            : "asc"
                                          : "none"
                                      }
                                      className="w-2.5 h-4"
                                    />
                                  </span>
                                </span>
                              </th>
                              <th
                                scope="col"
                                className="px-3 py-2 w-20 font-light text-sm text-center text-white dark:text-white tracking-wider rounded-tr-lg"
                              >
                                Action
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {teacherPassages.map((passage, index) => (
                              <tr
                                key={passage.teacherPassageId}
                                className={
                                  index % 2 === 0 ? "bg-white" : "bg-gray-100"
                                }
                              >
                                <td className="px-3 py-2 text-sm font-normal text-gray-500">
                                  {passage.teacherPassageId}
                                </td>
                                <td className="px-3 py-2 text-sm font-normal text-gray-500">
                                  {passage.createdDate
                                    ? new Date(
                                        passage.createdDate
                                      ).toLocaleDateString()
                                    : ""}
                                </td>
                                <td className="px-3 py-2 text-sm font-normal text-gray-700">
                                  {passage.title}
                                </td>
                                <td className="px-3 py-2 text-sm font-normal text-gray-500">
                                  {passage.gradeName}
                                </td>
                                <td className="px-3 py-2 text-sm font-normal text-gray-500">
                                  {passage.wordCount}
                                </td>
                                <td className="px-3 py-2 text-sm text-center font-normal text-gray-500">
                                  <span
                                    className={
                                      passage.isActive
                                        ? "inline-block w-2 h-2 rounded-full bg-green-500"
                                        : "inline-block w-2 h-2 rounded-full bg-red-500"
                                    }
                                  />
                                </td>
                                <td className="px-3 py-2 text-sm text-center font-normal text-gray-500">
                                  <div className="flex justify-center">
                                    {passage.isApproved ? (
                                      <ApproveIcon />
                                    ) : (
                                      <Disapprove />
                                    )}
                                  </div>
                                </td>
                                <td className="px-3 py-2 text-sm text-center font-normal text-gray-500">
                                  <InitialsBadge
                                    firstName={passage.ownerFirstName}
                                    lastName={passage.ownerLastName}
                                    userId={passage.ownerUserId}
                                  />
                                </td>
                                <td className="px-3 py-2 text-sm text-center font-normal text-gray-500">
                                  <InitialsBadge
                                    firstName={passage.authorFirstName}
                                    lastName={passage.authorLastName}
                                    userId={passage.authorUserId ?? null}
                                  />
                                </td>
                                <td className="px-3 py-2 text-sm text-center font-normal text-gray-500">
                                  <div className="flex justify-center gap-2">
                                    <EditIcon
                                      title="Edit passage"
                                      onClick={() =>
                                        navigate(
                                          `/crm/users/${userId}/content/passages/${passage.teacherPassageId}`
                                        )
                                      }
                                      className="h-5 w-5 text-xs font-light cursor-pointer hover:text-secondary-color transaction ease-in-out duration-300"
                                    />
                                    <DeleteIcon
                                      title="Delete passage"
                                      onClick={() =>
                                        handleDeleteClick(
                                          passage.teacherPassageId
                                        )
                                      }
                                      className="h-5 w-5 text-xs font-light cursor-pointer hover:text-red-500 transaction ease-in-out duration-300"
                                    />
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center mt-2">
                    {totalPages > 1 && (
                      <Pagination
                        pages={totalPages}
                        setPageNumber={setPageNumber}
                        ref={paginationRef}
                      />
                    )}
                    <span className="ml-auto">
                      <span className="text-sm pr-4 ml-auto text-left-700">
                        Showing {pageNumber} to{" "}
                        {totalRecords < pageSize ? totalRecords : pageSize} of{" "}
                        {totalRecords} results
                      </span>
                      <SelectPageSize
                        setPageSize={setPageSize}
                        type={"second"}
                        ref={pageSizeRef}
                        defaultPage={defaultPageSize}
                      />
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
